






















/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/camelcase */
import { Component, Prop, Watch } from 'vue-property-decorator';
import Editor from '@tinymce/tinymce-vue';
import { mixins } from 'vue-class-component';
import { QUESION_TYPES, MAX_BLANKS_COUNT } from '@/enums';
import ObjectSize from '../mixins/objectSize.mixin';
import Toaster from '../mixins/toaster.mixin';

@Component({
  components: {
    editor: Editor,
  },
})
export default class RichTextComponent extends mixins(ObjectSize, Toaster) {
  @Prop({ default: '' }) readonly initContent!: string;

  @Prop({ default: true }) readonly required!: boolean;

  @Prop({ default: false }) readonly validateOnInit!: boolean;

  @Prop(Number) readonly maxLength!: number;

  @Prop(Number) readonly richTextIndex!: number;

  @Prop(String) readonly questionType!: string;

  @Prop(Number) readonly answerWordsCount!: number;

  config: any = {
    menubar: false,
    branding: false,
    toolbar:
      `${this.questionType === QUESION_TYPES.FILL_BLANKS.id ? 'addblank | ' : ''}`
      + 'undo redo | '
      + 'removeformat | '
      + 'formatselect fontsizeselect | '
      + 'bold italic underline subscript superscript strikethrough blockquote| '
      + 'forecolor backcolor | '
      + 'outdent indent | '
      + 'alignleft aligncenter alignright alignjustify | '
      + 'ltr rtl | '
      + 'numlist bullist checklist | '
      + 'quickimage mathjax',
    plugins: [
      'advlist autolink lists image charmap print preview anchor',
      'searchreplace visualblocks code fullscreen directionality',
      'insertdatetime media table paste code help wordcount quickbars mathjax wordcount paste',
    ],
    min_height: 100,
    paste_data_images: true,
    quickbars_insert_toolbar: false,
    quickbars_selection_toolbar: 'bold italic underline strikethrough blockquote | formatselect',
    language_url: '/tinymce/langs/ar.js',
    mathjax: {
      lib: '/mathJax/es5/tex-mml-chtml.js', // required path to mathjax
    },
    setup: this.setupTinyMCE,
    content_style: `
      body {
        counter-reset: my-counter;
      }

      .app-blank-placeholder {
        border: 2px solid black;
        font-weight: bold;
        padding: 0px 3px 2px 3px;
      }

      .app-blank-placeholder::before {
        content: "blank ";
      }

      .app-blank-placeholder::after {
        counter-increment: my-counter;
        content: counter(my-counter);
      }

      body[dir="rtl"] .app-blank-placeholder::before {
        content: "فراغ ";
      }

    `,
  };

  configClearFormatting = {
    formats: {
      removeformat: [
        {
          selector: 'b,strong,em,i,font,u,strike,s,sub,sup,dfn,code,samp,kbd,var,cite,mark,q,del,ins,small', remove: 'empty', split: true, expand: false, block_expand: true, deep: true,
        },
        {
          selector: 'span', attributes: ['style'], remove: false, split: true, expand: false, block_expand: true, deep: true,
        },
        {
          selector: '*', attributes: ['style'], split: false, expand: false, block_expand: false, deep: false,
        },
      ],
    },
  };

  private setupTinyMCE(editor: { ui: { registry: { addButton: Function } } }): void {
    if (this.questionType === QUESION_TYPES.FILL_BLANKS.id) {
      editor.ui.registry.addButton('addblank', {
        text: this.$t('buttons.ADD_BLANK'),
        icon: 'plus',
        onAction: this.onClickAddBlankHandler,
      });
    }
  }

  getBlanksCount(): number {
    const blanks = this.editorRef.getDoc().querySelectorAll('span.app-blank-placeholder');
    return blanks ? Array.from(blanks).length : 0;
  }

  @Watch('content')
  onContentChangeHandler(): void {
    if (this.questionType === QUESION_TYPES.FILL_BLANKS.id) {
      this.content = this.content?.replaceAll(
        /<p><span\sclass="app-blank-placeholder">/g,
        '<p>&nbsp;<span class="app-blank-placeholder">'
      );
    }
  }

  /**
   * Handles the click event to add a blank placeholder.
   * If there are less than 3 existing placeholders, a new one is added.
   *
   * @instance
   * @private
   * @function
   * @memberof RichTextComponent
   */
  private onClickAddBlankHandler() {
    if (this.getBlanksCount() < MAX_BLANKS_COUNT) {
      // Add a non-breaking space before the new span
      this.editorRef.selection.setNode(
        this.editorRef
          .getDoc()
          .createRange()
          .createContextualFragment('&nbsp;'),
      );

      // Create the span element
      const spanElement = this.editorRef.getDoc().createElement('span');
      spanElement.className = 'app-blank-placeholder';

      // Insert the span element at the current cursor position
      const range = this.editorRef.selection.getRng();
      range.insertNode(spanElement);

      // Create a new range for the cursor after the span element
      const newRange = this.editorRef.getDoc().createRange();
      newRange.setStartAfter(spanElement);
      newRange.collapse(false);

      // Set the selection to the new range
      this.editorRef.selection.setRng(newRange);

      // Add an additional non-breaking space after the new span
      this.editorRef.selection.setNode(
        this.editorRef
          .getDoc()
          .createRange()
          .createContextualFragment('&nbsp;'),
      );

      this.valid = true;
      this.error = '';
    }

    const textareaElement = this.editorRef.getDoc().querySelector('.mce-content-body');
    textareaElement.focus();

    this.$emit('validContent', {
      content: this.content,
      valid: this.valid,
      richTextIndex: this.richTextIndex,
      error: this.error,
      contentCharCount: this.editorRef.plugins.wordcount.body.getCharacterCount(),
    });
  }

  content = this.initContent;

  editorRef: any = null;

  valid = true;

  error = '';

  get init() {
    return this.questionType === QUESION_TYPES.FILL_BLANKS.id
      ? {
        ...this.config,
        ...this.configClearFormatting,
        verify_html: false,
        language: this.lang,
      }
      : {
        ...this.config,
        language: this.lang,
      };
  }

  get lang() {
    return this.$store.state.lang.lang;
  }

  refreshInitContent() {
    if (this.editorRef) {
      this.editorRef.setContent(this.initContent);
    }
  }

  hasContent() {
    const charCount = this.editorRef !== null ? this.editorRef.plugins.wordcount.body.getCharacterCount() : 0;
    const noSpaceCharCount = this.editorRef !== null
      ? this.editorRef.plugins.wordcount.body.getCharacterCountWithoutSpaces()
      : 0;

    const hasContent = noSpaceCharCount > 0
      || this.content.trim().includes('img')
      || (this.questionType === QUESION_TYPES.FILL_BLANKS.id && this.getBlanksCount());
    if (hasContent) {
      if (charCount <= this.maxLength) {
        this.valid = true;
        this.error = '';
      } else {
        this.valid = false;
        this.error = 'richTextMaxLength';
      }
    } else {
      this.valid = false;
      this.error = 'requiredField';
    }

    this.$emit('validContent', {
      content: this.content,
      valid: this.valid,
      richTextIndex: this.richTextIndex,
      error: this.error,
      contentCharCount: this.editorRef.plugins.wordcount.body.getCharacterCount(),
    });
    return hasContent;
  }

  validWordsCount() {
    if (
      this.answerWordsCount === undefined
      || this.answerWordsCount < 0
      || this.answerWordsCount > 500
      || this.editorRef.plugins.wordcount.body.getWordCount() <= this.answerWordsCount
    ) {
      this.valid = true;
      this.error = '';
    } else {
      this.valid = false;
      this.error = 'maxWords';
    }
    this.$emit('validContent', {
      content: this.content,
      valid: this.valid,
      richTextIndex: this.richTextIndex,
      error: this.error,
    });
  }

  @Watch('$store.state.lang.lang', { deep: true })
  onLangChanged(lang) {
    localStorage.setItem('latex-editor-lang', lang);
  }

  @Watch('answerWordsCount')
  onAnswerWordsCountChange() {
    this.validWordsCount();
  }

  onInit(event, editor) {
    // FIXME this is very inefficient
    // it is better to "submit" editor content rather than emitting it on every change
    this.editorRef = editor;
    if (this.validateOnInit) {
      this.hasContent();
      this.validWordsCount();
    }
    editor.on('GetContent', (e) => {
      if (!e.selection) {
        const charCount = editor.plugins.wordcount.body.getCharacterCount();
        const noSpaceCharCount = editor.plugins.wordcount.body.getCharacterCountWithoutSpaces();
        let sentContent = e.content;
        // If it has text or has an image.
        if (noSpaceCharCount > 0 || e.content.trim().includes('img')) {
          if (charCount > this.maxLength) {
            this.valid = false;
            this.error = 'richTextMaxLength';
          } else if (this.answerWordsCount !== undefined) {
            this.validWordsCount();
          } else {
            this.valid = true;
            this.error = '';
          }
        } else if (this.questionType === QUESION_TYPES.FILL_BLANKS.id && this.getBlanksCount()) {
          this.valid = true;
          this.error = '';
        } else if (this.required) {
          this.valid = false;
          this.error = 'requiredField';
        } else {
          sentContent = '';
        }

        if (this.questionType === QUESION_TYPES.FILL_BLANKS.id) {
          const blanksCount = this.getBlanksCount();
          if (blanksCount < 1) {
            this.valid = false;
            this.error = 'validations.MUST_HAVE_ONE_BLANK';
          } else if (blanksCount > 3) {
            this.valid = false;
            this.error = 'validations.CANNOT_ADD_BLANKS';
          }
        }

        this.$emit('validContent', {
          content: sentContent,
          valid: this.valid,
          richTextIndex: this.richTextIndex,
          error: this.error,
          contentCharCount: this.editorRef.plugins.wordcount.body.getCharacterCount(),
        });
      }
    });
    // Work around to fix select all delete issue.
    editor.on('setContent', (e) => {
      if (e.content === '') {
        this.valid = false;
        this.error = 'requiredField';
        this.$emit('validContent', {
          content: e.content,
          valid: this.valid,
          richTextIndex: this.richTextIndex,
          error: this.error,
          contentCharCount: this.editorRef.plugins.wordcount.body.getCharacterCount(),
        });
      }
    });
    // Work around to limit uploaded file size.
    editor.on('BeforeSetContent', (e) => {
      if (e.content && e.content.includes('blob:')) {
        const blobURI = e.content
          .substr(e.content.indexOf('blob'), e.content.length)
          .replace('/>', '')
          .replace('>', '')
          .replace('"', '')
          .trim();
        if (e.target.editorUpload.blobCache.getByUri(blobURI)) {
          const allowedSize = 3 * 1048576; // 3MB
          const contentSize = this.getObjectSize(this.content);
          const fileSize = e.target.editorUpload.blobCache.getByUri(blobURI).blob().size;
          const convertedFileSize = Math.round(4 * (fileSize / 3));
          if (contentSize + convertedFileSize > allowedSize) {
            this.errorToaster({
              ar: 'تم إلغاء تحميل الملف لأنه سيتجاوز الحد الأقصى للسعة وهو 3 ميجابايت',
              en: 'File upload cancelled because it will exceed the max capacity limit of 3 MB',
            });
            e.preventDefault();
            e.stopPropagation();
          }
        }
      }
    });
  }
}
